// react
import { useEffect } from "react";
// libs
import { motion, Transition, Variants } from "framer-motion";
import { Link } from "react-router-dom";
import styled from "styled-components";
// components
import { Box } from "../Box";
import ButtonText from "../buttons/ButtonText";
import { FlexContainer, FlexItem } from "../Flex";
import { ImageFill } from "../ImageFill";
import { Text } from "../Text";
// other
import { ImageConfig } from "../../models/client";
import { fill } from "../../styles/utils";

interface SceneFocusProps {
  image: ImageConfig;
  title: string;
  slug: string;
  onCloseTrigger: () => any;
}

const Wrapper = styled.div`
  ${fill()}
  z-index: 10;
  padding: 80px 40px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.white};
`;

const Container = styled(motion.div)`
  display: flex;
  max-width: 1000px;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  pointer-events: none;
  pointer-events: all;
  flex: 1;
`;

const ImgContainer = styled.div`
  padding-top: 100%;
  position: relative;
  border-radius: 50%;
  overflow: hidden;
`;

const Blur = styled(motion.div)`
  ${fill()}
  z-index: -1;
  backdrop-filter: blur(8px);
`;

const transitionIn: Transition = { duration: 0.6, type: "tween" };
const transitionOut: Transition = { duration: 0.6, type: "tween" };

const blurVariants: Variants = {
  initial: { opacity: 0 },
  enter: { opacity: 1, transition: transitionIn },
  exit: {
    opacity: 0,
    transition: { ...transitionOut, delay: 0.3 },
  },
};

const contentVariants: Variants = {
  initial: { opacity: 0 },
  enter: { opacity: 1, transition: { ...transitionIn, delay: 0.3 } },
  exit: { opacity: 0, transition: transitionOut },
};

function SceneFocus({ image, title, slug, onCloseTrigger }: SceneFocusProps) {
  const onKeyUp = (e: KeyboardEvent) => {
    if (e.keyCode === 27 && onCloseTrigger) {
      onCloseTrigger();
    }
  };

  useEffect(() => {
    window.addEventListener("keyup", onKeyUp, false);
    return () => {
      window.removeEventListener("keyup", onKeyUp, false);
    };
  });

  return (
    <Wrapper>
      <Blur
        variants={blurVariants}
        initial="initial"
        animate="enter"
        exit="exit"
        onClick={() => onCloseTrigger()}
      ></Blur>
      <Container
        variants={contentVariants}
        initial="initial"
        animate="enter"
        exit="exit"
      >
        <FlexContainer
          flex="0 0 80%"
          flexWrap="wrap"
          margin={["-8px", null, -32]}
          justifyContent="center"
          alignItems="center"
        >
          <FlexItem
            flex={["0 0 90%", null, "0 0 40%"]}
            maxWidth={320}
            padding={["8px", null, 32]}
          >
            <ImgContainer>
              <ImageFill src={image.src || ""} />
            </ImgContainer>
          </FlexItem>
          <FlexItem
            flex={["0 0 100%", null, "0 0 60%"]}
            padding={["8px", null, 32]}
          >
            <Box mb={[3, 4]}>
              <Text variant="h2">{title}</Text>
            </Box>
            <Link to={"/themes/" + slug}>
              <ButtonText icon="arrowForward">Explore collection</ButtonText>
            </Link>
          </FlexItem>
        </FlexContainer>
      </Container>
    </Wrapper>
  );
}

export default SceneFocus;
